import {
  Hct,
  SchemeContent,
  SchemeExpressive,
  SchemeFidelity,
  SchemeFruitSalad,
  SchemeMonochrome,
  SchemeNeutral,
  SchemeRainbow,
  SchemeTonalSpot,
  SchemeVibrant,
  argbFromHex,
  hexFromArgb,
} from "@material/material-color-utilities";

import { SelectedTheme, TypeTheme } from "@revolt/state/stores/Theme";

/**
 * Generate the Material variables from the given properties
 *
 * Currently only generates color keys
 */
export function createMaterialColourVariables<P extends string>(
  theme: SelectedTheme,
  prefix: P,
): addPrefixToObject<MaterialColours, P> {
  switch (theme.preset) {
    case "you":
      // Locked to the fixed Whitemane scheme (see WHITEMANE_COLOURS). The accent /
      // variant / darkMode inputs are intentionally ignored.
      return Object.entries(WHITEMANE_COLOURS).reduce(
        (d, [key, value]) => ({
          ...d,
          [`${prefix}${key}`]: value,
        }),
        {} as addPrefixToObject<MaterialColours, P>,
      );
    default:
      return {} as never;
  }
}

/**
 * Create R,G,B triplets for MDUI variables
 */
export function createMduiColourTriplets<P extends string>(
  theme: SelectedTheme,
  prefix: P,
): addPrefixToObject<MaterialColours, P> {
  const variables = createMaterialColourVariables(theme, prefix);

  for (const key in variables) {
    const [_, r, g, b] = /#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})/i.exec(
      variables[key as keyof typeof variables] as string,
    )!;

    variables[key as keyof typeof variables] =
      `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}` as never;
  }

  return variables;
}

type addPrefixToObject<T, P extends string> = {
  [K in keyof T as K extends string ? `${P}${K}` : never]: T[K];
};

type _addSuffixToObject<T, S extends string> = {
  [K in keyof T as K extends string ? `${K}${S}` : never]: T[K];
};

type MaterialColours = {
  primary: string;
  "on-primary": string;
  "primary-container": string;
  "on-primary-container": string;
  secondary: string;
  "on-secondary": string;
  "secondary-container": string;
  "on-secondary-container": string;
  tertiary: string;
  "on-tertiary": string;
  "tertiary-container": string;
  "on-tertiary-container": string;
  error: string;
  "on-error": string;
  "error-container": string;
  "on-error-container": string;

  "primary-fixed": string;
  "primary-fixed-dim": string;
  "on-primary-fixed": string;
  "on-primary-fixed-variant": string;
  "secondary-fixed": string;
  "secondary-fixed-dim": string;
  "on-secondary-fixed": string;
  "on-secondary-fixed-variant": string;
  "tertiary-fixed": string;
  "tertiary-fixed-dim": string;
  "on-tertiary-fixed": string;
  "on-tertiary-fixed-variant": string;

  "surface-dim": string;
  surface: string;
  "surface-bright": string;

  "surface-container-lowest": string;
  "surface-container-low": string;
  "surface-container": string;
  "surface-container-high": string;
  "surface-container-highest": string;

  "on-surface": string;
  "on-surface-variant": string;
  outline: string;
  "outline-variant": string;

  "inverse-surface": string;
  "inverse-on-surface": string;
  "inverse-primary": string;

  scrim: string;
  shadow: string;
};

/**
 * Fixed "Whitemane" colour scheme mapped onto the Material 3 token set so the Stoat
 * client matches the game launcher: dark charcoal surfaces, gold primary, blue
 * secondary. The client is locked to this single theme; the Material You generator
 * below is kept for reference / easy revert but is no longer called. Every value MUST
 * be 6-digit hex - createMduiColourTriplets() parses #RRGGBB into RGB triplets.
 */
const WHITEMANE_COLOURS: MaterialColours = {
  // Primary = Whitemane gold (accent only - used for buttons/checkboxes/focus,
  // never as a decorative background). The *-container tokens are deliberately
  // NEUTRAL surfaces: M3 uses them as tonal backgrounds all over the app, and the
  // launcher never tints backgrounds with the accent colour.
  primary: "#FFD000",
  "on-primary": "#000000",
  "primary-container": "#2B2C34",
  "on-primary-container": "#E8E8ED",
  // Secondary = electric blue (accent only)
  secondary: "#0E6AF4",
  "on-secondary": "#FFFFFF",
  "secondary-container": "#2B2C34",
  "on-secondary-container": "#E8E8ED",
  // Tertiary (accent only)
  tertiary: "#E0C266",
  "on-tertiary": "#1A1400",
  "tertiary-container": "#2B2C34",
  "on-tertiary-container": "#E8E8ED",
  // Error = coral red
  error: "#FF6B6B",
  "on-error": "#3D0709",
  "error-container": "#5A1B1B",
  "on-error-container": "#FFD9D9",

  // Fixed tones (consumed by a few M3 components)
  "primary-fixed": "#FFE08A",
  "primary-fixed-dim": "#FFD000",
  "on-primary-fixed": "#251A00",
  "on-primary-fixed-variant": "#5A4700",
  "secondary-fixed": "#D4E3FF",
  "secondary-fixed-dim": "#0E6AF4",
  "on-secondary-fixed": "#00183A",
  "on-secondary-fixed-variant": "#0A4AA8",
  "tertiary-fixed": "#F2DFA8",
  "tertiary-fixed-dim": "#E0C266",
  "on-tertiary-fixed": "#1A1400",
  "on-tertiary-fixed-variant": "#5A4700",

  // Surfaces (Whitemane charcoal hierarchy: app -> cards -> inputs -> hover).
  // Lifted ~5% lightness across the whole ramp (Discord-adjacent brightness)
  // while keeping the hue and the relative steps between levels intact.
  "surface-dim": "#1A1B20",
  surface: "#1E1F25",
  "surface-bright": "#3A3B44",
  "surface-container-lowest": "#17181D",
  "surface-container-low": "#23242B",
  "surface-container": "#26272E",
  "surface-container-high": "#2B2C34",
  "surface-container-highest": "#33343D",

  // Text + outlines (variant/outlines lifted with the surfaces so contrast
  // ratios stay roughly where they were). NOTE: in M3 semantics `outline` is
  // a LEGIBLE grey (reference dark theme: #938F99) - upstream components use
  // it for faint text (settings category titles, timestamps, dividers), so
  // it must stay readable; only outline-variant is the subtle border tone.
  "on-surface": "#E8E8ED",
  "on-surface-variant": "#9A9CA6",
  outline: "#767883",
  "outline-variant": "#383941",

  // Inverse (snackbars, tooltips)
  "inverse-surface": "#E8E8ED",
  "inverse-on-surface": "#26272E",
  "inverse-primary": "#5A4700",

  scrim: "#000000",
  shadow: "#000000",
};

/**
 * Generate a Material You colour scheme
 * @param accent Accent colour in hex format
 * @param darkMode Dark mode
 * @param constrat Constrast level
 * @returns Material colours
 */
function generateMaterialYouScheme(
  accent: string,
  darkMode: boolean,
  contrast: number,
  variant: TypeTheme["m3Variant"],
): MaterialColours {
  const hct = Hct.fromInt(argbFromHex(accent));

  let scheme;
  switch (variant) {
    case "content":
      scheme = new SchemeContent(hct, darkMode, contrast);
      break;
    case "expressive":
      scheme = new SchemeExpressive(hct, darkMode, contrast);
      break;
    case "fidelity":
      scheme = new SchemeFidelity(hct, darkMode, contrast);
      break;
    case "fruit_salad":
      scheme = new SchemeFruitSalad(hct, darkMode, contrast);
      break;
    case "monochrome":
      scheme = new SchemeMonochrome(hct, darkMode, contrast);
      break;
    case "neutral":
      scheme = new SchemeNeutral(hct, darkMode, contrast);
      break;
    case "rainbow":
      scheme = new SchemeRainbow(hct, darkMode, contrast);
      break;
    case "vibrant":
      scheme = new SchemeVibrant(hct, darkMode, contrast);
      break;
    case "tonal_spot":
    default:
      scheme = new SchemeTonalSpot(hct, darkMode, contrast);
      break;
  }

  return {
    primary: hexFromArgb(scheme.primary),
    "on-primary": hexFromArgb(scheme.onPrimary),
    "primary-container": hexFromArgb(scheme.primaryContainer),
    "on-primary-container": hexFromArgb(scheme.onPrimaryContainer),
    secondary: hexFromArgb(scheme.secondary),
    "on-secondary": hexFromArgb(scheme.onSecondary),
    "secondary-container": hexFromArgb(scheme.secondaryContainer),
    "on-secondary-container": hexFromArgb(scheme.onSecondaryContainer),
    tertiary: hexFromArgb(scheme.tertiary),
    "on-tertiary": hexFromArgb(scheme.onTertiary),
    "tertiary-container": hexFromArgb(scheme.tertiaryContainer),
    "on-tertiary-container": hexFromArgb(scheme.onTertiaryContainer),
    error: hexFromArgb(scheme.error),
    "on-error": hexFromArgb(scheme.onError),
    "error-container": hexFromArgb(scheme.errorContainer),
    "on-error-container": hexFromArgb(scheme.onErrorContainer),

    "primary-fixed": hexFromArgb(scheme.primaryFixed),
    "primary-fixed-dim": hexFromArgb(scheme.primaryFixedDim),
    "on-primary-fixed": hexFromArgb(scheme.onPrimaryFixed),
    "on-primary-fixed-variant": hexFromArgb(scheme.onPrimaryFixedVariant),
    "secondary-fixed": hexFromArgb(scheme.secondaryFixed),
    "secondary-fixed-dim": hexFromArgb(scheme.onSecondaryFixed),
    "on-secondary-fixed": hexFromArgb(scheme.onSecondaryFixed),
    "on-secondary-fixed-variant": hexFromArgb(scheme.onSecondaryFixedVariant),
    "tertiary-fixed": hexFromArgb(scheme.tertiaryFixed),
    "tertiary-fixed-dim": hexFromArgb(scheme.tertiaryFixedDim),
    "on-tertiary-fixed": hexFromArgb(scheme.onTertiaryFixed),
    "on-tertiary-fixed-variant": hexFromArgb(scheme.onTertiaryFixedVariant),

    "surface-dim": hexFromArgb(scheme.surfaceDim),
    surface: hexFromArgb(scheme.surface),
    "surface-bright": hexFromArgb(scheme.surfaceBright),

    "surface-container-lowest": hexFromArgb(scheme.surfaceContainerLowest),
    "surface-container-low": hexFromArgb(scheme.surfaceContainerLow),
    "surface-container": hexFromArgb(scheme.surfaceContainer),
    "surface-container-high": hexFromArgb(scheme.surfaceContainerHigh),
    "surface-container-highest": hexFromArgb(scheme.surfaceContainerHighest),

    "on-surface": hexFromArgb(scheme.onSurface),
    "on-surface-variant": hexFromArgb(scheme.onSurfaceVariant),
    outline: hexFromArgb(scheme.outline),
    "outline-variant": hexFromArgb(scheme.outlineVariant),

    "inverse-surface": hexFromArgb(scheme.inverseSurface),
    "inverse-on-surface": hexFromArgb(scheme.inverseOnSurface),
    "inverse-primary": hexFromArgb(scheme.inversePrimary),

    scrim: hexFromArgb(scheme.scrim),
    shadow: hexFromArgb(scheme.shadow),
  };
}
